import { Board } from "./board";
import { directionFromVector } from "./direction";
import type { DistanceMap } from "./distanceMap";
import { Event } from "./events/event";
import { EventParameter } from "./events/eventParameter";
import { EventType } from "./events/eventType";
import { createGameFlags, type GameFlags } from "./gameFlags";
import { GameStats } from "./gameStats";
import type { Player } from "./player";
import { type PlayerCommand, getMoveCommandForDirection } from "./playerCommand";
import { Sound } from "./things/sounds/sound";
import { SoundId } from "./things/sounds/soundId";
import { XY } from "./xy";
import { getRandomEmptyCell } from "./levelgenerator/levelGeneratorUtils";
import { cyrb128, generateSeedString, seededRng, sfc32 } from "./random";

/**
 * Level transitions are a two-phase handshake between the engine and the UI so that the UI
 * can animate an iris wipe around the (potentially slow) level generation.
 *
 *   1. `beginLevelTransition(n)` — called from the turn-processing path (via
 *      `LevelChangerThing`). Records the request in `levelTransition`; does NOT mutate world
 *      state. The UI starts the iris close on the OLD board.
 *   2. `completeLevelTransition()` — called by the UI once the iris is fully closed. Generates
 *      the target level if needed, swaps the player onto the new board, dispatches arrival
 *      hooks, fills in `toCenterXY` on the signal. The UI then starts the iris open centered
 *      on `toCenterXY` and clears the signal.
 *
 * `autoCompleteTransitions` defaults to true so headless callers (tests, balance tool,
 * anything driving the engine without an iris) work without extra setup. The UI flips it off
 * (in `launchGame`) so the two-phase animation gets to drive the timing.
 */
export interface LevelTransitionSignal {
	/** Level number the player is transitioning to. */
	targetLevelNumber: number;
	/** Player position on the old board (center of the closing iris). */
	fromCenterXY: XY;
	/** Player position on the new board (center of the opening iris). Null until complete runs. */
	toCenterXY: XY | null;
}

export class Game {
	private levels: Map<number, Board> = new Map();
	currentLevelNumber: number = 0;
	player: Player | null = null;
	gameFlags: GameFlags = createGameFlags();
	stats: GameStats = new GameStats();
	private events: Event[] = [];
	private autoMoveCommandQueue: PlayerCommand[] = [];
	seed: string;
	runId: string;
	mapRng: () => number;
	levelGeneratorFn: ((levelNumber: number, mapRng: () => number) => Board) | null = null;
	isTutorial: boolean = false;
	/** Loadout preset chosen at the character select screen; null for modes without one (tutorial, experimental). */
	characterId: string | null = null;
	weaponHintsSeen: Set<string> = new Set();
	/** Player-scoped (NOT serialized): persisted via localStorage by the UI bridge. */
	seenHints: Set<string> = new Set();

	levelTransition: LevelTransitionSignal | null = null;

	/**
	 * When true, `beginLevelTransition` immediately calls `completeLevelTransition`. Defaults
	 * to true so headless callers (tests, balance tool) work without extra setup; the UI flips
	 * this off in `launchGame` so transitions can animate in two phases.
	 */
	autoCompleteTransitions: boolean = true;

	constructor(seedStr?: string) {
		const finalSeed = seedStr && seedStr.length > 0 ? seedStr : generateSeedString();
		this.seed = finalSeed;
		this.runId = crypto.randomUUID();
		console.log("Game Seed:", finalSeed);
		const mapSeed = finalSeed + "_map";
		const s2 = cyrb128(mapSeed);
		this.mapRng = sfc32(s2[0], s2[1], s2[2], s2[3]);
	}

	/** Independent RNG per level — see docs/level-generation-seeding.md. */
	mapRngForLevel(levelNumber: number): () => number {
		return seededRng(`${this.seed}_map_${levelNumber}`);
	}

	getCurrentBoard(): Board {
		const board = this.levels.get(this.currentLevelNumber);
		if (!board) throw new Error(`Level ${this.currentLevelNumber} not found`);
		return board;
	}

	addLevel(levelNumber: number, board: Board): void {
		this.levels.set(levelNumber, board);
		board.setGame(this);
		board.levelChanger = this;
	}

	/**
	 * Start a level transition: record intent and let the UI begin the iris close on the
	 * current board. Does not generate, swap, or move the player — `completeLevelTransition`
	 * does that once the iris is fully closed so any synchronous generation stutter stays
	 * behind a black screen.
	 *
	 * If `autoCompleteTransitions` is true, completes inline (used by headless drivers).
	 */
	beginLevelTransition(levelNumber: number): void {
		this.player!.setAutoMoving(false);
		this.autoMoveCommandQueue.length = 0;

		this.levelTransition = {
			targetLevelNumber: levelNumber,
			fromCenterXY: this.player!.xy,
			toCenterXY: null,
		};

		if (this.autoCompleteTransitions) {
			this.completeLevelTransition();
		}
	}

	/**
	 * Complete the transition kicked off by `beginLevelTransition`: generate the target level
	 * if needed, remove the player from the old board, place them on the new board, dispatch
	 * arrival hooks. Idempotent — calling after complete has already run is a no-op.
	 */
	completeLevelTransition(): void {
		if (!this.levelTransition) throw new Error("No pending level transition to complete");
		if (this.levelTransition.toCenterXY !== null) return;

		const levelNumber = this.levelTransition.targetLevelNumber;
		const fromLevelNumber = this.currentLevelNumber;
		const currentBoard = this.levels.get(this.currentLevelNumber);

		if (currentBoard) {
			this.stats.updateTurnsForLevel(fromLevelNumber, currentBoard.turnCount);
			currentBoard.removeThing(this.player!);
			this.player!.active = true;
		}

		const isNewLevel = !this.levels.has(levelNumber);
		let targetBoard = this.levels.get(levelNumber);
		if (!targetBoard) {
			if (!this.levelGeneratorFn) throw new Error(`Level ${levelNumber} not found`);
			const generated = this.levelGeneratorFn(levelNumber, this.mapRngForLevel(levelNumber));
			this.addLevel(levelNumber, generated);
			targetBoard = generated;
		}
		if (isNewLevel) {
			this.stats.recordLevelVisited();
		}

		this.currentLevelNumber = levelNumber;

		const returnChanger = targetBoard.findLevelChangerTo(fromLevelNumber);
		let playerXY: XY;
		if (returnChanger) {
			playerXY = returnChanger.xy;
		} else if (targetBoard.initialSpawnXY) {
			playerXY = targetBoard.initialSpawnXY;
		} else {
			playerXY = getRandomEmptyCell(targetBoard, this.mapRng) ?? new XY(0, 0);
		}
		this.player!.xy = playerXY;

		targetBoard.dispatchPlayerArrival();
		targetBoard.addThing(this.player!);
		targetBoard.addNonPhysicalThing(new Sound(SoundId.ZOOM));

		if (targetBoard.areaName) {
			const event = new Event(EventType.REACHED_AREA);
			event.setParameter(EventParameter.AREA_NAME, targetBoard.areaName);
			targetBoard.addEvent(event);
		}

		this.levelTransition.toCenterXY = playerXY;
	}

	/** Begin + complete in one call. Used by tests and the balance tool, which don't drive a UI. */
	moveToLevel(levelNumber: number): void {
		this.beginLevelTransition(levelNumber);
		this.completeLevelTransition();
	}

	autoMoveTo(target: XY, distanceMap: DistanceMap): void {
		this.autoMoveCommandQueue.length = 0;
		const path = distanceMap.rollDownhill(target);
		if (path.length === 0) {
			this.player!.setAutoMoving(false);
			this.getCurrentBoard().addEvent(new Event(EventType.NO_PATH_FOR_AUTOMOVE));
			return;
		}
		let current = target;
		for (const xy of path) {
			const delta = current.subtract(xy);
			const dir = directionFromVector(delta);
			if (dir !== null) {
				this.autoMoveCommandQueue.push(getMoveCommandForDirection(dir));
			}
			current = xy;
		}
		this.player!.setAutoMoving(true);
	}

	followAutoMove(): void {
		if (!this.player!.isAutoMoving() || this.autoMoveCommandQueue.length === 0) return;
		const cmd = this.autoMoveCommandQueue.pop()!;
		this.player!.setCommand(cmd);
		this.processTurnInternal();
		if (this.autoMoveCommandQueue.length === 0) {
			this.player!.setAutoMoving(false);
		}
	}

	isAutoMoving(): boolean {
		return this.player!.isAutoMoving();
	}

	isGameOver(): boolean {
		return this.player!.hp <= 0;
	}

	addEvent(event: Event): void {
		this.events.push(event);
	}

	/** Returns true the first time `weaponKey` is recorded this run; false on subsequent calls. */
	markWeaponHintShown(weaponKey: string): boolean {
		if (this.weaponHintsSeen.has(weaponKey)) return false;
		this.weaponHintsSeen.add(weaponKey);
		return true;
	}

	/**
	 * Engine-side once-per-player hint emission. Skips in tutorial. Dedups via `seenHints`,
	 * which is populated from localStorage at construction (see UI's `hintStorage`).
	 */
	emitHint(hint: { id: string; stringKey: string }): void {
		if (this.isTutorial) return;
		if (this.seenHints.has(hint.id)) return;
		this.seenHints.add(hint.id);
		const event = new Event(EventType.HINT);
		event.setParameter(EventParameter.HINT_STRING_KEY, hint.stringKey);
		this.getCurrentBoard().addEvent(event);
	}

	resetSeenHints(): void {
		this.seenHints.clear();
	}

	processTurn(): void {
		this.player!.setAutoMoving(false);
		this.autoMoveCommandQueue.length = 0;
		this.processTurnInternal();
	}

	private processTurnInternal(): void {
		this.events = [];
		this.getCurrentBoard().processTurn();
		this.stats.updateTurnsForLevel(this.currentLevelNumber, this.getCurrentBoard().turnCount);
	}

	getEvents(): Event[] {
		return this.events;
	}

	serialize(): Record<string, unknown> {
		const levels: Record<string, unknown> = {};
		for (const [num, board] of this.levels) {
			levels[String(num)] = board.serialize();
		}
		return {
			currentLevelNumber: this.currentLevelNumber,
			levels,
			gameFlags: { ...this.gameFlags },
			stats: this.stats.serialize(),
			seed: this.seed,
			runId: this.runId,
			isTutorial: this.isTutorial,
			characterId: this.characterId,
			weaponHintsSeen: Array.from(this.weaponHintsSeen),
		};
	}

	static deserialize(data: any): Game {
		const boardEntries: [number, Board][] = Object.entries(data.levels).map(
			([key, val]) => [Number(key), Board.deserialize(val)] as [number, Board],
		);

		const currentEntry = boardEntries.find(([n]) => n === data.currentLevelNumber);
		if (!currentEntry) throw new Error("Current level not found");

		const player = currentEntry[1].player;
		if (!player) throw new Error("Player not found on current level's board");

		const seed = data.seed;
		const game = new Game(seed);
		game.player = player;
		game.runId = data.runId ?? crypto.randomUUID();
		game.currentLevelNumber = data.currentLevelNumber;
		game.isTutorial = data.isTutorial === true;
		game.characterId = typeof data.characterId === "string" ? data.characterId : null;
		game.gameFlags = { ...createGameFlags(), ...(data.gameFlags ?? {}) };
		if (data.stats) {
			game.stats = GameStats.deserialize(data.stats);
		}

		for (const [num, board] of boardEntries) {
			game.addLevel(num, board);
		}

		if (Array.isArray(data.weaponHintsSeen)) {
			for (const key of data.weaponHintsSeen) {
				if (typeof key === "string") game.weaponHintsSeen.add(key);
			}
		}

		return game;
	}
}
